// Contenu de .chatbot-messages : message d'accueil, bulles, cartes d'action et
// indicateur de chargement (balisage visé par chatbot_widget.css).
import type { MouseEvent } from 'react'
import { t } from '@/lib/i18n'
import { retryButtonHtml, texts, warn } from './format'
import type { ChatItem, SystemAction } from './types'

// `items` : nombre de points de la liste. La page /chatbot n'en montre que 5
// (sans « visite guidée »).
export function WelcomeMessage({ items = 6 }: { items?: number }) {
  return (
    <div className="welcome-message">
      <p>{t('chatbot.widget.welcome_greeting')}</p>
      <ul>
        {Array.from({ length: items }, (_, i) => i + 1).map((i) => (
          <li key={i}>{t(`chatbot.widget.welcome_item_${i}`)}</li>
        ))}
      </ul>
      <p>{t('chatbot.widget.welcome_footer')}</p>
    </div>
  )
}

export function LoadingIndicator({ text }: { text: string }) {
  return (
    <div id="loading-indicator" className="loading-indicator">
      <div className="loading-animation">
        <div className="loading-spinner"></div>
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <span className="loading-text">{text}</span>
    </div>
  )
}

type TextItem = Extract<ChatItem, { kind: 'text' }>
type ActionItem = Extract<ChatItem, { kind: 'action' }>

// Le bouton « réessayer » est le dernier enfant de .message-content, après le
// HTML du message (il compte pour `.message-content p:last-child`).
export function TextMessage({ item, onRetry }: { item: TextItem; onRetry: (content: string | null) => void }) {
  const html = item.retryContent ? item.html + retryButtonHtml(item.retryContent) : item.html
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest('.retry-btn')) onRetry(item.retryContent)
  }
  return (
    <div className={`message ${item.role}${item.error ? ' error' : ''}`}>
      <div className="message-content" dangerouslySetInnerHTML={{ __html: html }} onClick={item.retryContent ? handleClick : undefined} />
      <div className="message-time">{item.time}</div>
    </div>
  )
}

export function ActionCard({
  item,
  onExecute,
}: {
  item: ActionItem
  onExecute: (key: number, action: SystemAction, messageId: string | null) => void
}) {
  const { action, state } = item
  const done = state === 'started' || state === 'error'
  const className = done
    ? `message assistant system-action-completed${state === 'error' ? ' error' : ''}`
    : 'message assistant system-action'
  const isTutorial = action.type === 'launch_tutorial'

  let content
  if (state === 'started') {
    content = <div className="system-action-started">✅ {item.resultText}</div>
  } else if (state === 'error') {
    content = (
      <div className="system-action-error">
        {warn(item.resultText || texts.error())}
        {item.upgradeUrl && (
          <a href={item.upgradeUrl} className="btn btn-sm btn-primary mt-2" style={{ display: 'block', marginTop: '8px', textAlign: 'center' }}>
            {texts.upgradeNow()}
          </a>
        )}
      </div>
    )
  } else {
    content = (
      <button
        className={`btn system-action-btn ${isTutorial ? 'system-action-btn--tutorial' : 'btn-primary'}${state === 'loading' ? ' loading' : ''}`}
        type="button"
        data-message-id={item.messageId ?? undefined}
        disabled={state === 'loading'}
        onClick={() => onExecute(item.key, action, item.messageId)}
      >
        <i className={`fas ${isTutorial ? 'fa-graduation-cap' : 'fa-play-circle'}`}></i>{' '}
        <span className="action-title">{action.title || texts.runAction()}</span>
        {action.subtitle && <div className="action-subtitle">{action.subtitle}</div>}
      </button>
    )
  }

  return (
    <div className={className} data-message-id={item.messageId ?? undefined}>
      <div className="message-content system-action-content">{content}</div>
      <div className="message-time">{item.time}</div>
    </div>
  )
}

export default function ChatMessages({
  items,
  showWelcome,
  loadingText,
  onRetry,
  onExecute,
}: {
  items: ChatItem[]
  showWelcome: boolean
  loadingText: string | null
  onRetry: (content: string | null) => void
  onExecute: (key: number, action: SystemAction, messageId: string | null) => void
}) {
  return (
    <>
      {showWelcome && <WelcomeMessage />}
      {items.map((item) =>
        item.kind === 'text' ? (
          <TextMessage key={item.key} item={item} onRetry={onRetry} />
        ) : (
          <ActionCard key={item.key} item={item} onExecute={onExecute} />
        ),
      )}
      {loadingText !== null && <LoadingIndicator text={loadingText} />}
    </>
  )
}
